import { makeAutoObservable } from 'mobx';
import React from 'react';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { CampaignStatusType, PlayerTypeType, PlayerTypeZod } from 'src/api_openapi/v2/bonus-engine/schemas';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import {
    isNumericBetween,
    isNumericPrecission,
    isNumericRelationWithInput,
    isNumericRelationWithNumber,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { Common } from 'src/AppState/Common';
import { PromoCodesState } from 'src/domains/casino/webview/bonus-engine/campaign/promo-code/PromoCodes.state';
import {
    EligibleUsersState,
    EligibleUsersType,
} from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsers.state';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { GeneralDetailsState } from 'src/domains/casino/webview/bonus-engine/campaign/general-details/GeneralDetails.state';

interface AllPlayersType {
    value: PlayerTypeType;
    label: string;
    onClick?: () => void;
}

export type PlayerManagementFormType = {
    'player-type': string;
    'min-customer-sf': string;
    'max-customer-sf': string;
};

export class PlayerManagementState {
    public playerType: FormInputState<string, string>;
    public minCustomerSf: FormInputState<string, string>;
    public maxCustomerSf: FormInputState<string, string>;
    public campaignStatus: CampaignStatusType | null = null;
    public isCloneCampaign: boolean = false;
    public readonly eligibleUsersState: EligibleUsersState;

    private form: FormModel<PlayerManagementFormType>;

    private readonly noEmptyValidationMessage = 'Required field';

    public constructor(
        private readonly common: Common,
        private readonly promoCodesState: PromoCodesState,
        private readonly generalDetailsState: GeneralDetailsState
    ) {
        this.eligibleUsersState = new EligibleUsersState(this.common);

        this.playerType = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map((value) => {
                const baseErrorMessage = `'New Players' can be picked when at least one promo code is added`;

                if (value === 'new-players' && this.promoCodesState.isEmpty()) {
                    return this.generalDetailsState.optInOnly === true
                        ? Result.createOk(value)
                        : Result.createError(`${baseErrorMessage} or 'Require User Opt-In' has been selected`);
                }
                return Result.createOk(value);
            });
        this.minCustomerSf = FormInputState.new('')
            .map(isNumericBetween({ from: 0.01, to: 100, optional: true }))
            .map(isNumericPrecission(2, false, 'Enter a decimal value. Min is 0.01'));
        this.maxCustomerSf = FormInputState.new('')
            .map(isNumericRelationWithNumber(0.01, 'more', true, () => false, 'Enter a decimal value. Max is 100'))
            .map(isNumericRelationWithNumber(100, 'less', true, () => false, 'Enter a decimal value. Max is 100'))
            .map(
                isNumericRelationWithInput(
                    () => this.minCustomerSf,
                    'more',
                    true,
                    true,
                    'Enter a decimal value. Max is 100'
                )
            )
            .map(isNumericPrecission(2, true, 'Enter a decimal value. Min is 0.01'));

        this.form = FormModel.group({
            'player-type': this.playerType,
            'min-customer-sf': this.minCustomerSf,
            'max-customer-sf': this.maxCustomerSf,
        });
        makeAutoObservable(this);
    }

    public get eligibleUsers(): EligibleUsersType {
        return this.eligibleUsersState.eligibleUsers;
    }

    public validateForm(): boolean {
        this.form.setAsVisited();

        let isFormValid = this.form.result.value.type === 'ok';

        if (this.isSegmentedPlayerSelected) {
            isFormValid = isFormValid && this.eligibleUsersState.validateForm();
        }

        return isFormValid;
    }

    public validNotEmpty(): void {
        this.form.mapChild((item) => {
            if (item instanceof FormInputState) {
                const value = item.value;
                if (value.trim() !== '') {
                    item.setAsVisited();
                }
            }

            return item;
        });
    }

    public get formErrorsCount(): number {
        let formErorsCount = this.form.errorsForView.length;

        if (this.isSegmentedPlayerSelected) {
            formErorsCount += this.eligibleUsersState.formErrorsCount;
        }

        return formErorsCount;
    }

    public get allPlayersOptions(): Array<AllPlayersType> {
        const playerTypeToLabel: Record<PlayerTypeType, string> = {
            'all-players': 'All Players',
            'new-players': 'New Players',
            'segmented-customers': 'Segmented Customers',
        };

        const onClick = async (): Promise<boolean> => {
            if (this.isSegmentedPlayerSelected && this.eligibleUsersState.totalAdded > 0) {
                return await this.handleChangePlayerType();
            }

            return true;
        };

        return PlayerTypeZod.options.map((optionSchema) => {
            const type = optionSchema.value;

            return {
                value: type,
                label: playerTypeToLabel[type],
                onClick,
            };
        });
    }

    public get isSegmentedPlayerSelected(): boolean {
        return this.playerType.value === 'segmented-customers';
    }

    public resetAllInputs = (): void => {
        this.maxCustomerSf.reset();
        this.minCustomerSf.reset();
        this.playerType.reset();

        this.eligibleUsersState.removeAllPlayerIds();
    };

    public get playerManagement(): PlayerTypeType | undefined {
        const playerType = this.playerType.value;

        if (playerType === '') {
            return undefined;
        }

        const asType = PlayerTypeZod.safeParse(playerType);
        if (asType.success === false) {
            return undefined;
        }
        return asType.data;
    }

    public get minSf(): number | undefined {
        return this.parseSF(this.minCustomerSf);
    }

    public get maxSf(): number | undefined {
        return this.parseSF(this.maxCustomerSf);
    }

    private parseSF(input: FormInputState<string, string>): number | undefined {
        const result = input.result.value;

        if (result.type === 'error') {
            return undefined;
        }

        const parsedNumber = parseFloat(result.data);
        if (isNaN(parsedNumber)) {
            return undefined;
        }
        return parsedNumber;
    }

    public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        this.campaignStatus = campaignModel.status;

        const minStakeFactor = campaignModel.data.minStakeFactor ?? '';
        const maxStakeFactor = campaignModel.data.maxStakeFactor ?? '';
        this.playerType.setValue(campaignModel.playerType ?? '');
        this.minCustomerSf.setValue(String(minStakeFactor));
        this.maxCustomerSf.setValue(String(maxStakeFactor));

        const eligibleUsers = this.isSegmentedPlayerSelected ? (campaignModel.eligibleUsers ?? []) : [];

        this.eligibleUsersState.setFormValues(eligibleUsers);
    };

    public isTouched(): boolean {
        return this.form.isTouched() || this.eligibleUsersState.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
        this.eligibleUsersState.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
        this.eligibleUsersState.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
        this.eligibleUsersState.resetIsVisited();
    }

    private handleChangePlayerType = (): Promise<boolean> => {
        return new Promise((resolve) => {
            const popupsStack = PopupsStackState.get(this.common); // pass common if needed

            popupsStack.addPopup('change_player_type', {
                template: 'generic',
                title: 'Change Player Type',
                message: (
                    <p className='m-0'>
                        Are you sure you want to change the player type? This action can not be undone.
                    </p>
                ),
                hasOverlay: true,
                cancel_label: 'Cancel',
                confirm_label: 'Continue',
                onConfirm: () => {
                    this.eligibleUsersState.removeAllPlayerIds();

                    popupsStack.closePopupOnTop();
                    resolve(true);
                },
                onCancel: () => {
                    popupsStack.closePopupOnTop();
                    resolve(false);
                },
            });
        });
    };
}
